import { HttpErrorResponse } from "@angular/common/http";
import type { ErrorEvent, EventHint, StackFrame } from "@sentry/angular";

/**
 * An HTTP status of `0` means the browser never received a response: the user is
 * offline, the request was blocked by CORS/DNS, or it was aborted by a page
 * navigation or reload. None of these are application faults, so they are
 * dropped instead of flooding GlitchTip.
 */
const NETWORK_ERROR_PATTERNS = [/^Error Code: 0\b/, /^Http failure response for .*: 0\b/];

/**
 * Scripts a browser extension injects into the page share our `window`, so any
 * promise they leave unhandled is captured by Sentry's global handlers and filed
 * against this project. Their failures are neither reproducible nor fixable from
 * here, so events raised purely inside injected code are dropped.
 */
const BROWSER_EXTENSION_PROTOCOLS = [
  "chrome-extension://",
  "moz-extension://",
  "safari-extension://",
  "safari-web-extension://",
  "extensions::",
];

export function shouldDropEvent(event: ErrorEvent, hint: EventHint): boolean {
  if (isNetworkError(event, hint)) return true;

  return isBrowserExtensionError(event);
}

function isNetworkError(event: ErrorEvent, hint: EventHint): boolean {
  const originalException = hint.originalException;
  if (originalException instanceof HttpErrorResponse) return originalException.status === 0;

  return collectErrorMessages(event, originalException).some((message) =>
    NETWORK_ERROR_PATTERNS.some((pattern) => pattern.test(message))
  );
}

function isBrowserExtensionError(event: ErrorEvent): boolean {
  const frames = collectStackFrames(event);
  if (frames.length === 0) return false;

  return frames.every(isBrowserExtensionFrame);
}

function isBrowserExtensionFrame(frame: StackFrame): boolean {
  const source = frame.filename ?? frame.abs_path;
  if (!source) return false;

  return BROWSER_EXTENSION_PROTOCOLS.some((protocol) => source.startsWith(protocol));
}

function collectStackFrames(event: ErrorEvent): StackFrame[] {
  return (event.exception?.values ?? []).flatMap((value) => value.stacktrace?.frames ?? []);
}

/**
 * Gathers every string the failure could be described by, since the message ends
 * up either on the thrown value or on the already-serialized Sentry event
 * depending on where the error was raised.
 */
function collectErrorMessages(event: ErrorEvent, originalException: unknown): string[] {
  const messages: (string | undefined)[] = [
    event.message,
    ...(event.exception?.values ?? []).map((value) => value.value),
  ];

  if (typeof originalException === "string") messages.push(originalException);
  else if (originalException instanceof Error) messages.push(originalException.message);

  return messages.filter((message): message is string => Boolean(message));
}
